import React, { useCallback, useMemo } from 'react';

import {
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
} from '@ionic/react';

import Typography from 'components/Typography';
import { useIsIncluded } from 'hooks/useIsIncluded';
import { DateRangeFormatter, useCurrencyFormat } from 'intl';
import { getRoute } from 'scenes/FinancePage/IO/constants';
import {
  ACTION_APPROVE,
  ACTION_MORE_INFO,
  ACTION_REJECT,
  ACTION_UNDO,
} from 'scenes/FinancePage/IO/constants';
import {
  useApprove,
  useMoreInfo,
  useReject,
  useUndo,
} from 'scenes/FinancePage/IO/data/mutations';
import { COLOR_MAP } from 'scenes/FinancePage/IO/utils/color';
import Avatar from 'scenes/FinancePage/components/Avatar';
import { AVATAR_BY_ACTION_TYPE_MAP } from 'scenes/FinancePage/constants';
import { IIOData } from 'scenes/FinancePage/interfaces';

interface IItem {
  item: IIOData;
}

const IOItem: React.FC<IItem> = ({ item }) => {
  const [approve] = useApprove();
  const [reject] = useReject();
  const [undo] = useUndo();
  const [moreInfo] = useMoreInfo();

  const {
    id,
    actionType,
    businessEntities,
    businessPartner,
    status,
    period,
    budget,
    monthlyBudget,
    parentIo,
    metadata,
  }: any = useMemo(() => {
    return item?.data;
  }, [item?.data]);

  const formatTotalBudget = useCurrencyFormat({
    currency: budget.currencyCode,
  });
  const formatMonthlyBudget = useCurrencyFormat({
    currency: monthlyBudget.currencyCode,
  });

  const detailsLine = useMemo(() => {
    return `Budget: ${formatTotalBudget(budget.amount)} / ${formatMonthlyBudget(
      monthlyBudget.amount
    )} monthly`;
  }, [formatTotalBudget, budget, formatMonthlyBudget, monthlyBudget]);

  const isActionAllowed = useIsIncluded(item?.data?.actions);

  const allowedActionUndo = isActionAllowed(ACTION_UNDO);
  const allowedActionMoreInfo = isActionAllowed(ACTION_MORE_INFO);
  const allowedActionApprove = isActionAllowed(ACTION_APPROVE);
  const allowedActionReject = isActionAllowed(ACTION_REJECT);
  const primaryActions = allowedActionUndo || allowedActionMoreInfo;
  const secondaryActions = allowedActionUndo || allowedActionMoreInfo;

  const handleApprove = useCallback(() => {
    approve({ id, status });
  }, [id, status, approve]);

  const handleReject = useCallback(() => {
    reject({
      id,
      businessPartner,
      period,
      createdBy: metadata?.createdBy,
      parentIo,
    });
  }, [id, businessPartner, period, metadata?.createdBy, parentIo, reject]);

  const handleUndo = useCallback(() => {
    undo({ id });
  }, [id, undo]);

  const handleMoreInfo = useCallback(() => {
    moreInfo({
      id,
      businessPartner,
      period,
      createdBy: metadata?.createdBy,
      parentIo,
    });
  }, [id, businessPartner, period, metadata?.createdBy, parentIo, moreInfo]);

  return (
    <IonItemSliding key={`${primaryActions}_${secondaryActions}`}>
      {primaryActions ? (
        <IonItemOptions side={'start'}>
          {allowedActionUndo ? (
            <IonItemOption color={'medium'} onClick={handleUndo}>
              UNDO
            </IonItemOption>
          ) : null}
          {allowedActionMoreInfo ? (
            <IonItemOption color={'primary'} onClick={handleMoreInfo}>
              MORE INFO
            </IonItemOption>
          ) : null}
        </IonItemOptions>
      ) : null}
      <IonItem
        routerLink={getRoute(id)}
        routerDirection={'forward'}
        button={true}
        detail={false}
      >
        <Avatar
          slot={'start'}
          color={COLOR_MAP.get(status?.id)}
          type={AVATAR_BY_ACTION_TYPE_MAP.get(actionType)}
        >
          IO
        </Avatar>
        <div>
          <Typography variant={'overline'} color={'medium'}>
            <DateRangeFormatter value={period} template={'MONTH'} /> / #{id}
          </Typography>
          <Typography variant={'subtitle1'} color={'primary'}>
            {businessEntities?.map((el: any) => el.name).join(', ')}
          </Typography>
          <Typography>{detailsLine}</Typography>
        </div>
      </IonItem>
      {secondaryActions ? (
        <IonItemOptions side={'end'}>
          {allowedActionApprove ? (
            <IonItemOption color={'success'} onClick={handleApprove}>
              APPROVE
            </IonItemOption>
          ) : null}
          {allowedActionReject ? (
            <IonItemOption color={'danger'} onClick={handleReject}>
              REJECT
            </IonItemOption>
          ) : null}
        </IonItemOptions>
      ) : null}
    </IonItemSliding>
  );
};

export default IOItem;
